import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import * as taskboardService from '../services/taskboardService';
import * as requirementService from '../services/requirementService';
import * as commentService from '../services/commentService';
import { listUsers } from '../services/userService';
import RichTextEditor from '../components/RichTextEditor';

const STORY_TYPES = ['bug', 'task', 'spike'];
const PRIORITIES = ['critical', 'high', 'medium', 'low'];

interface Activity {
  id: string;
  action_type: string;
  description: string;
  metadata?: string;
  created_at: string;
  user_first_name?: string;
  user_last_name?: string;
  user_email?: string;
}

export function TaskBoardRequirementDetail(): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token, user } = useAuth();

  const [requirement, setRequirement] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Section collapse state (all open by default)
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const toggleSection = (key: string) => setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));

  // Editable fields
  const [editData, setEditData] = useState({
    description: '',
    story_type: '',
    story_points: '' as string | number,
    priority: '',
    assigned_to: '',
    labels: '',
    due_date: '',
    original_estimate_hours: '' as string | number,
  });
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  // Company users for assignee dropdown
  const [companyUsers, setCompanyUsers] = useState<any[]>([]);

  // Time logging
  const [timeLogs, setTimeLogs] = useState<any[]>([]);
  const [newLogHours, setNewLogHours] = useState('');
  const [newLogDescription, setNewLogDescription] = useState('');
  const [loggingTime, setLoggingTime] = useState(false);

  // Comments / Change Log toggle
  const [activeTab, setActiveTab] = useState<'comments' | 'changelog'>('comments');
  const [comments, setComments] = useState<commentService.RequirementComment[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [editingComment, setEditingComment] = useState<string | null>(null);
  const [editCommentText, setEditCommentText] = useState('');

  // Checklist
  const [checklistItems, setChecklistItems] = useState<any[]>([]);
  const [newChecklistTitle, setNewChecklistTitle] = useState('');
  const [checklistSubmitting, setChecklistSubmitting] = useState(false);

  // Acceptance Criteria
  const [acceptanceCriteria, setAcceptanceCriteria] = useState<any[]>([]);
  const [newCriterionTitle, setNewCriterionTitle] = useState('');
  const [criterionSubmitting, setCriterionSubmitting] = useState(false);

  // Attachments
  const [attachments, setAttachments] = useState<any[]>([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState<any>(null);

  // Subtasks
  const [subtasks, setSubtasks] = useState<any[]>([]);
  const [subtasksLoading, setSubtasksLoading] = useState(false);
  const [quickAddActive, setQuickAddActive] = useState(false);
  const [quickAddTitle, setQuickAddTitle] = useState('');
  const [quickAddSubmitting, setQuickAddSubmitting] = useState(false);
  const [subtaskEditingField, setSubtaskEditingField] = useState<{ id: string; field: string } | null>(null);

  // Sprint History
  const [sprintHistory, setSprintHistory] = useState<any[]>([]);

  // Load requirement detail
  useEffect(() => {
    async function loadData() {
      if (!token || !id) return;
      setLoading(true);
      try {
        const [reqRes, usersRes] = await Promise.all([
          taskboardService.getRequirementDetail(id, token),
          listUsers({ page: 1, limit: 500, status: 'active' }, token).catch(() => ({ users: [] })),
        ]);

        const req = reqRes.data;
        setRequirement(req);
        setCompanyUsers(usersRes.users || []);

        setEditData({
          description: req.description || '',
          story_type: req.story_type || '',
          story_points: req.story_points ?? '',
          priority: req.priority || '',
          assigned_to: req.assigned_to || '',
          labels: req.labels || '',
          due_date: req.due_date ? new Date(req.due_date).toISOString().split('T')[0] : '',
          original_estimate_hours: req.original_estimate_hours ?? '',
        });
      } catch (err: any) {
        console.error('Error loading requirement:', err);
        setError(err.response?.data?.error || 'Failed to load requirement');
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [token, id]);

  // Load time logs
  useEffect(() => {
    if (token && id) loadTimeLogs();
  }, [token, id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load checklist items
  useEffect(() => {
    if (token && id) loadChecklistItems();
  }, [token, id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load acceptance criteria
  useEffect(() => {
    if (token && id) loadAcceptanceCriteria();
  }, [token, id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load attachments
  useEffect(() => {
    if (token && id) loadAttachments();
  }, [token, id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load comments
  useEffect(() => {
    if (token && id && requirement?.project_id) loadComments();
  }, [token, id, requirement?.project_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load activities
  useEffect(() => {
    if (token && id) loadActivities();
  }, [token, id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadTimeLogs() {
    if (!token || !id) return;
    try {
      const res = await taskboardService.getTimeLogs(id, token);
      setTimeLogs(res.data || []);
    } catch (err) {
      console.error('Error loading time logs:', err);
    }
  }

  async function loadChecklistItems() {
    if (!token || !id) return;
    try {
      const res = await taskboardService.getChecklistItems(id, token);
      setChecklistItems(res.data || []);
    } catch (err) {
      console.error('Error loading checklist items:', err);
    }
  }

  async function handleAddChecklistItem() {
    if (!token || !id || !newChecklistTitle.trim()) return;
    setChecklistSubmitting(true);
    try {
      await taskboardService.createChecklistItem({ requirement_id: id, title: newChecklistTitle.trim() }, token);
      setNewChecklistTitle('');
      await loadChecklistItems();
      loadActivities();
    } catch (err) {
      console.error('Error creating checklist item:', err);
    } finally {
      setChecklistSubmitting(false);
    }
  }

  async function handleToggleChecklistItem(itemId: string) {
    if (!token) return;
    try {
      await taskboardService.toggleChecklistItem(itemId, token);
      await loadChecklistItems();
      loadActivities();
    } catch (err) {
      console.error('Error toggling checklist item:', err);
    }
  }

  async function handleDeleteChecklistItem(itemId: string) {
    if (!token) return;
    try {
      await taskboardService.deleteChecklistItem(itemId, token);
      await loadChecklistItems();
      loadActivities();
    } catch (err) {
      console.error('Error deleting checklist item:', err);
    }
  }

  async function loadAcceptanceCriteria() {
    if (!token || !id) return;
    try {
      const res = await taskboardService.getAcceptanceCriteria(id, token);
      setAcceptanceCriteria(res.data || []);
    } catch (err) {
      console.error('Error loading acceptance criteria:', err);
    }
  }

  async function handleAddCriterion() {
    if (!token || !id || !newCriterionTitle.trim()) return;
    setCriterionSubmitting(true);
    try {
      await taskboardService.createAcceptanceCriterion({ requirement_id: id, title: newCriterionTitle.trim() }, token);
      setNewCriterionTitle('');
      await loadAcceptanceCriteria();
      loadActivities();
    } catch (err) {
      console.error('Error creating acceptance criterion:', err);
    } finally {
      setCriterionSubmitting(false);
    }
  }

  async function handleToggleCriterion(itemId: string) {
    if (!token) return;
    try {
      await taskboardService.toggleAcceptanceCriterion(itemId, token);
      await loadAcceptanceCriteria();
      loadActivities();
    } catch (err) {
      console.error('Error toggling acceptance criterion:', err);
    }
  }

  async function handleDeleteCriterion(itemId: string) {
    if (!token) return;
    try {
      await taskboardService.deleteAcceptanceCriterion(itemId, token);
      await loadAcceptanceCriteria();
      loadActivities();
    } catch (err) {
      console.error('Error deleting acceptance criterion:', err);
    }
  }

  async function loadAttachments() {
    if (!token || !id) return;
    try {
      const res = await taskboardService.getAttachments(id, token);
      setAttachments(res.data || []);
    } catch (err) {
      console.error('Error loading attachments:', err);
    }
  }

  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !token || !id) return;

    // Reset the input so the same file can be re-selected
    e.target.value = '';

    if (attachments.length >= 10) {
      setSaveMessage({ text: 'Maximum of 10 attachments per task', type: 'error' });
      setTimeout(() => setSaveMessage(null), 4000);
      return;
    }

    if (file.size > 1024 * 1024 * 1024) {
      setSaveMessage({ text: 'File exceeds the 1 GB size limit', type: 'error' });
      setTimeout(() => setSaveMessage(null), 4000);
      return;
    }

    setUploadingFile(true);
    try {
      await taskboardService.uploadAttachment(id, file, token);
      await loadAttachments();
      loadActivities();
      setSaveMessage({ text: `Uploaded "${file.name}"`, type: 'success' });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (err: any) {
      console.error('Error uploading file:', err);
      setSaveMessage({ text: err.response?.data?.error || 'Failed to upload file', type: 'error' });
      setTimeout(() => setSaveMessage(null), 4000);
    } finally {
      setUploadingFile(false);
    }
  }

  async function confirmDeleteAttachment() {
    if (!token || !attachmentToDelete) return;
    try {
      await taskboardService.deleteAttachment(attachmentToDelete.id, token);
      setAttachmentToDelete(null);
      await loadAttachments();
      loadActivities();
    } catch (err) {
      console.error('Error deleting attachment:', err);
      setAttachmentToDelete(null);
    }
  }

  function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  async function loadComments() {
    if (!token || !id || !requirement?.project_id) return;
    try {
      const data = await commentService.getComments(requirement.project_id, id, token);
      setComments(data || []);
    } catch (err) {
      console.error('Error loading comments:', err);
    }
  }

  async function loadActivities() {
    if (!token || !id) return;
    try {
      const res = await taskboardService.getRequirementActivities(id, token, 50);
      setActivities(res.data || []);
    } catch (err) {
      console.error('Error loading activities:', err);
    }
  }

  // Load sprint history
  useEffect(() => {
    if (token && id) loadSprintHistory();
  }, [token, id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadSprintHistory() {
    if (!token || !id) return;
    try {
      const res = await taskboardService.getRequirementSprintHistory(id, token);
      setSprintHistory(res.data || []);
    } catch (err) {
      console.error('Error loading sprint history:', err);
    }
  }

  // Load subtasks
  useEffect(() => {
    if (token && id && requirement && requirement.hierarchy_type !== 'child') loadSubtasks();
  }, [token, id, requirement?.hierarchy_type]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadSubtasks() {
    if (!token || !id) return;
    setSubtasksLoading(true);
    try {
      const res = await taskboardService.getRequirementChildren(id, token);
      setSubtasks(res.data || []);
    } catch (err) {
      console.error('Error loading subtasks:', err);
    } finally {
      setSubtasksLoading(false);
    }
  }

  async function handleQuickAddSubtask() {
    if (!token || !id || !quickAddTitle.trim() || !requirement?.project_id) return;
    setQuickAddSubmitting(true);
    try {
      const created = await requirementService.createRequirement(requirement.project_id, {
        title: quickAddTitle.trim(),
        requirement_type: 'functional',
        priority: 'medium',
        hierarchy_type: 'child',
        parent_requirement_id: id,
        approval_status: 'approved',
      }, token);
      // Set story_type to sub-task (board field, set via assign endpoint)
      if (created?.id) {
        await taskboardService.assignRequirement(created.id, { story_type: 'sub-task' }, token);
      }
      setQuickAddTitle('');
      setQuickAddActive(false);
      await loadSubtasks();
      loadActivities();
    } catch (err) {
      console.error('Error creating subtask:', err);
    } finally {
      setQuickAddSubmitting(false);
    }
  }

  async function handleSubtaskInlineEdit(subtaskId: string, field: string, value: string) {
    if (!token) return;
    setSubtaskEditingField(null);
    try {
      await taskboardService.assignRequirement(subtaskId, { [field]: value || undefined }, token);
      await loadSubtasks();
    } catch (err) {
      console.error('Error updating subtask:', err);
    }
  }

  function handleQuickAddKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleQuickAddSubtask();
    } else if (e.key === 'Escape') {
      setQuickAddActive(false);
      setQuickAddTitle('');
    }
  }

  async function handleSave() {
    if (!token || !id) return;
    setSaving(true);
    setSaveMessage(null);
    try {
      await taskboardService.assignRequirement(id, {
        description: editData.description,
        story_type: editData.story_type || undefined,
        story_points: editData.story_points !== '' ? Number(editData.story_points) : undefined,
        priority: editData.priority || undefined,
        assigned_to: editData.assigned_to || undefined,
        labels: editData.labels || undefined,
        due_date: editData.due_date || undefined,
        original_estimate_hours: editData.original_estimate_hours !== '' ? Number(editData.original_estimate_hours) : undefined,
      }, token);

      // Reload to get fresh data
      const reqRes = await taskboardService.getRequirementDetail(id, token);
      setRequirement(reqRes.data);

      // Reload activities to show the update
      loadActivities();

      setSaveMessage({ text: 'Changes saved', type: 'success' });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (err: any) {
      console.error('Error saving:', err);
      setSaveMessage({ text: err.response?.data?.error || 'Failed to save', type: 'error' });
    } finally {
      setSaving(false);
    }
  }

  async function handleLogTime() {
    if (!token || !id || !newLogHours || Number(newLogHours) <= 0) return;
    setLoggingTime(true);
    try {
      await taskboardService.logTime({
        requirement_id: id,
        hours: Number(newLogHours),
        description: newLogDescription || undefined,
      }, token);
      setNewLogHours('');
      setNewLogDescription('');
      await loadTimeLogs();
      loadActivities();

      const reqRes = await taskboardService.getRequirementDetail(id, token);
      setRequirement(reqRes.data);
    } catch (err) {
      console.error('Error logging time:', err);
    } finally {
      setLoggingTime(false);
    }
  }

  async function handleDeleteLog(logId: string) {
    if (!token || !id) return;
    try {
      await taskboardService.deleteTimeLog(logId, token);
      await loadTimeLogs();
      loadActivities();

      const reqRes = await taskboardService.getRequirementDetail(id, token);
      setRequirement(reqRes.data);
    } catch (err) {
      console.error('Error deleting log:', err);
    }
  }

  async function handleSubmitComment() {
    if (!token || !id || !requirement?.project_id || !newComment.trim()) return;
    setSubmittingComment(true);
    try {
      await commentService.createComment(requirement.project_id, id, { comment_text: newComment.trim() }, token);
      setNewComment('');
      await loadComments();
      loadActivities();
    } catch (err) {
      console.error('Error submitting comment:', err);
    } finally {
      setSubmittingComment(false);
    }
  }

  async function handleSubmitReply(parentId: string) {
    if (!token || !id || !requirement?.project_id || !replyText.trim()) return;
    try {
      await commentService.createComment(requirement.project_id, id, { comment_text: replyText.trim(), parent_comment_id: parentId }, token);
      setReplyingTo(null);
      setReplyText('');
      await loadComments();
    } catch (err) {
      console.error('Error submitting reply:', err);
    }
  }

  async function handleUpdateComment(commentId: string) {
    if (!token || !id || !requirement?.project_id || !editCommentText.trim()) return;
    try {
      await commentService.updateComment(requirement.project_id, id, commentId, { comment_text: editCommentText.trim() }, token);
      setEditingComment(null);
      setEditCommentText('');
      await loadComments();
    } catch (err) {
      console.error('Error updating comment:', err);
    }
  }

  async function handleDeleteComment(commentId: string) {
    if (!token || !id || !requirement?.project_id) return;
    if (!window.confirm('Delete this comment?')) return;
    try {
      await commentService.deleteComment(requirement.project_id, id, commentId, token);
      await loadComments();
    } catch (err) {
      console.error('Error deleting comment:', err);
    }
  }

  // Count total comments including replies
  function countComments(commentList: commentService.RequirementComment[]): number {
    let count = 0;
    for (const c of commentList) {
      if (!c.is_deleted) count++;
      if (c.replies) count += countComments(c.replies);
    }
    return count;
  }

  const commentCount = countComments(comments);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      </Layout>
    );
  }

  if (error || !requirement) {
    return (
      <Layout>
        <div className="p-6">
          <div className="bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg p-4">
            {error || 'Requirement not found'}
          </div>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 text-sm text-indigo-600 hover:text-indigo-800 dark:text-indigo-400"
          >
            Go back
          </button>
        </div>
      </Layout>
    );
  }

  const priorityColor: Record<string, string> = {
    critical: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    high: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    medium: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    low: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  };

  const statusColor: Record<string, string> = {
    draft: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    'in-progress': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    review: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    completed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    approved: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
  };

  function renderComment(comment: commentService.RequirementComment, depth = 0) {
    if (comment.is_deleted) {
      return (
        <div key={comment.id} className={`${depth > 0 ? 'ml-6 border-l-2 border-gray-200 dark:border-gray-700 pl-4' : ''}`}>
          <p className="text-sm text-gray-400 italic py-2">This comment has been deleted.</p>
          {comment.replies?.map(r => renderComment(r, depth + 1))}
        </div>
      );
    }

    const isOwner = comment.user_id === user?.id;

    return (
      <div key={comment.id} className={`${depth > 0 ? 'ml-6 border-l-2 border-gray-200 dark:border-gray-700 pl-4' : ''}`}>
        <div className="py-3">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
              {commentService.getUserInitials(comment.user_first_name, comment.user_last_name)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {comment.user_first_name} {comment.user_last_name}
                </span>
                <span className="text-xs text-gray-400">
                  {commentService.formatTimestamp(comment.created_at)}
                </span>
                {comment.is_edited && (
                  <span className="text-xs text-gray-400 italic">(edited)</span>
                )}
              </div>

              {editingComment === comment.id ? (
                <div className="mt-2">
                  <textarea
                    value={editCommentText}
                    onChange={e => setEditCommentText(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:outline-none resize-y"
                  />
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() => handleUpdateComment(comment.id)}
                      className="px-3 py-1 text-xs bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => { setEditingComment(null); setEditCommentText(''); }}
                      className="px-3 py-1 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <p className="mt-1 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                  {comment.comment_text}
                </p>
              )}

              {!editingComment && (
                <div className="flex items-center gap-3 mt-2">
                  <button
                    onClick={() => { setReplyingTo(comment.id); setReplyText(''); }}
                    className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
                  >
                    Reply
                  </button>
                  {isOwner && (
                    <>
                      <button
                        onClick={() => { setEditingComment(comment.id); setEditCommentText(comment.comment_text); }}
                        className="text-xs text-gray-500 dark:text-gray-400 hover:underline"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteComment(comment.id)}
                        className="text-xs text-red-500 hover:underline"
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              )}

              {replyingTo === comment.id && (
                <div className="mt-3">
                  <textarea
                    value={replyText}
                    onChange={e => setReplyText(e.target.value)}
                    rows={2}
                    placeholder="Write a reply..."
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:outline-none resize-y"
                  />
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() => handleSubmitReply(comment.id)}
                      disabled={!replyText.trim()}
                      className="px-3 py-1 text-xs bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                    >
                      Reply
                    </button>
                    <button
                      onClick={() => { setReplyingTo(null); setReplyText(''); }}
                      className="px-3 py-1 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        {comment.replies?.map(r => renderComment(r, depth + 1))}
      </div>
    );
  }

  return (
    <Layout>
      <div className="p-6">
        {/* Breadcrumbs */}
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 text-sm text-text-tertiary hover:text-text-primary transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          {requirement.project_name && (
            <>
              <span className="text-text-tertiary">/</span>
              <span className="text-sm text-text-secondary">{requirement.project_name}</span>
            </>
          )}
          {requirement.requirement_code && (
            <>
              <span className="text-text-tertiary">/</span>
              <span className="text-xs font-mono font-semibold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 px-2.5 py-0.5 rounded-full border border-indigo-200 dark:border-indigo-800">
                {requirement.requirement_code}
              </span>
            </>
          )}
        </div>

        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-blue-400 rounded-lg shadow-lg p-6 mb-6">
          <h1 className="text-2xl font-bold text-white break-words">{requirement.title}</h1>
        </div>

        {/* Key Info Bar */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          {requirement.story_type && (
            <div className="flex items-center gap-2 bg-gray-600 rounded-lg px-4 py-2">
              {requirement.story_type === 'story' && (
                <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
              )}
              {requirement.story_type === 'bug' && (
                <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              )}
              {requirement.story_type === 'task' && (
                <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              )}
              {requirement.story_type === 'epic' && (
                <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              )}
              {requirement.story_type === 'spike' && (
                <svg className="w-5 h-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              )}
              {requirement.story_type === 'sub-task' && (
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
              )}
              <span className="text-sm font-bold text-white uppercase tracking-wide">
                {requirement.story_type}
              </span>
            </div>
          )}

          <div className="flex items-center gap-2 bg-gray-600 rounded-lg px-4 py-2">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 text-white text-xs font-bold flex items-center justify-center">
              {requirement.assigned_to_name
                ? requirement.assigned_to_name.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2)
                : '?'
              }
            </div>
            <span className="text-sm font-bold text-white">
              {requirement.assigned_to_name || 'Unassigned'}
            </span>
          </div>

          <div className={`flex items-center gap-2 rounded-lg px-4 py-2 ${
            editData.due_date && new Date(editData.due_date) < new Date()
              ? 'bg-red-600'
              : 'bg-gray-600'
          }`}>
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="text-sm font-bold text-white">
              {editData.due_date
                ? new Date(editData.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                : 'No Due Date'
              }
            </span>
          </div>

          <div className="flex items-center gap-2 bg-gray-600 rounded-lg px-4 py-2">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <span className="text-sm font-bold text-white">
              {commentCount} {commentCount === 1 ? 'Comment' : 'Comments'}
            </span>
          </div>

          <div className="flex items-center gap-2 bg-gray-600 rounded-lg px-4 py-2">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
            </svg>
            <span className="text-sm font-bold text-white">
              {requirement.column_name || 'No Column'}
            </span>
          </div>

          {/* Save button - pushed to the right */}
          <button
            onClick={handleSave}
            disabled={saving}
            className="ml-auto px-5 py-2 bg-gradient-to-r from-indigo-600 to-blue-600 text-white text-sm font-medium rounded-full hover:from-indigo-700 hover:to-blue-700 disabled:opacity-50 transition-colors shadow-sm"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>

        {/* Save feedback */}
        {saveMessage && (
          <div className={`mb-4 px-4 py-2 rounded-lg text-sm font-medium ${
            saveMessage.type === 'success'
              ? 'bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800'
              : 'bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800'
          }`}>
            {saveMessage.text}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main content (left 2/3) */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description & Details combined */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center gap-2 cursor-pointer" onClick={() => toggleSection('details')}>
                <svg className={`h-5 w-5 text-text-tertiary transition-transform duration-200 ${!collapsed.details ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wider">Description & Details</h2>
              </div>
              {!collapsed.details && <>
              <div className="mt-3"></div>
              <RichTextEditor
                value={editData.description || ''}
                onChange={(html) => setEditData({ ...editData, description: html })}
                placeholder="Add a description..."
                minHeight="100px"
              />

              {/* Attachments */}
              <div className="mt-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">Attachments</label>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                    attachments.length >= 10
                      ? 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30'
                      : 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30'
                  }`}>
                    {attachments.length} / 10
                  </span>
                  <label className="ml-auto cursor-pointer">
                    <input
                      type="file"
                      className="hidden"
                      onChange={handleFileUpload}
                      disabled={uploadingFile || attachments.length >= 10}
                    />
                    <span className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                      attachments.length >= 10
                        ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                        : 'bg-indigo-600 text-white hover:bg-indigo-700 cursor-pointer'
                    }`}>
                      {uploadingFile ? (
                        <>
                          <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                          Uploading...
                        </>
                      ) : (
                        <>
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                          </svg>
                          Attach
                        </>
                      )}
                    </span>
                  </label>
                </div>
                {attachments.length > 0 ? (
                  <div className="space-y-1.5 mb-3">
                    {attachments.map((att: any) => (
                      <div key={att.id} className="flex items-center gap-2 group">
                        <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        <a
                          href={att.download_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 text-sm text-indigo-600 dark:text-indigo-400 hover:underline truncate"
                          title={att.original_name}
                        >
                          {att.original_name}
                        </a>
                        <span className="text-xs text-gray-400 flex-shrink-0">{formatFileSize(att.file_size)}</span>
                        <button
                          onClick={() => setAttachmentToDelete(att)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors flex-shrink-0"
                          title="Delete attachment"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          Delete
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 italic mb-3">No attachments yet</p>
                )}
              </div>

              <div className="mt-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">Acceptance Criteria</label>
                  {acceptanceCriteria.length > 0 && (
                    <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 px-2 py-0.5 rounded-full">
                      {acceptanceCriteria.filter((c: any) => c.is_passed).length}/{acceptanceCriteria.length} passed
                    </span>
                  )}
                </div>
                {acceptanceCriteria.length > 0 ? (
                  <div className="space-y-1.5 mb-3">
                    {acceptanceCriteria.map((item: any) => (
                      <div key={item.id} className="flex items-center gap-2 group">
                        <input
                          type="checkbox"
                          checked={item.is_passed}
                          onChange={() => handleToggleCriterion(item.id)}
                          className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                        />
                        <span className={`flex-1 text-sm ${
                          item.is_passed
                            ? 'line-through text-gray-400 dark:text-gray-500'
                            : 'text-gray-900 dark:text-gray-100'
                        }`}>
                          {item.title}
                        </span>
                        <button
                          onClick={() => handleDeleteCriterion(item.id)}
                          className="text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                          title="Remove"
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 italic mb-3">No acceptance criteria yet</p>
                )}
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={newCriterionTitle}
                    onChange={e => setNewCriterionTitle(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleAddCriterion();
                      }
                    }}
                    disabled={criterionSubmitting}
                    className="flex-1 px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                    placeholder="Add criterion..."
                  />
                  <button
                    onClick={handleAddCriterion}
                    disabled={criterionSubmitting || !newCriterionTitle.trim()}
                    className="px-3 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-md hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                  >
                    {criterionSubmitting ? '...' : 'Add'}
                  </button>
                </div>
              </div>

              <div className="mt-6 mb-4"></div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Story Type</label>
                  <select
                    value={editData.story_type}
                    onChange={e => setEditData({ ...editData, story_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  >
                    <option value="">None</option>
                    {STORY_TYPES.map(t => (
                      <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Priority</label>
                  <select
                    value={editData.priority}
                    onChange={e => setEditData({ ...editData, priority: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  >
                    {PRIORITIES.map(p => (
                      <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Story Points</label>
                  <input
                    type="number"
                    min="0"
                    value={editData.story_points}
                    onChange={e => setEditData({ ...editData, story_points: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                    placeholder="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Labels</label>
                  <input
                    type="text"
                    value={editData.labels}
                    onChange={e => setEditData({ ...editData, labels: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                    placeholder="frontend, api"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Original Estimate (h)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.25"
                    value={editData.original_estimate_hours}
                    onChange={e => setEditData({ ...editData, original_estimate_hours: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                    placeholder="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Sprint</label>
                  <span className="inline-block px-3 py-2 text-sm rounded-md font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400">
                    {requirement.sprint_name || 'Not in Sprint'}
                  </span>
                </div>

              </div>
              </>}
            </div>

            {/* Subtasks Section */}
            {requirement.hierarchy_type !== 'child' && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center gap-2 cursor-pointer mb-4" onClick={() => toggleSection('subtasks')}>
                  <svg className={`h-5 w-5 text-text-tertiary transition-transform duration-200 ${!collapsed.subtasks ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wider">Subtasks</h2>
                  <div className="flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-r from-indigo-100 to-blue-100 border border-indigo-200 dark:from-indigo-900/30 dark:to-blue-900/30 dark:border-indigo-800">
                    <span className="text-xs font-semibold text-indigo-700 dark:text-indigo-400">{subtasks.length}</span>
                  </div>
                </div>

                {!collapsed.subtasks && <>
                {subtasksLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
                  </div>
                ) : subtasks.length > 0 ? (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-700 text-left">
                        <th className="pb-2 text-xs font-medium text-gray-500 dark:text-gray-400">Title</th>
                        <th className="pb-2 text-xs font-medium text-gray-500 dark:text-gray-400 w-20">Type</th>
                        <th className="pb-2 text-xs font-medium text-gray-500 dark:text-gray-400 w-24">Priority</th>
                        <th className="pb-2 text-xs font-medium text-gray-500 dark:text-gray-400 w-12 text-center">Pts</th>
                        <th className="pb-2 text-xs font-medium text-gray-500 dark:text-gray-400 w-36">Assignee</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subtasks.map(subtask => (
                        <tr
                          key={subtask.id}
                          className="border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30 cursor-pointer"
                          onClick={() => navigate(`/taskboard/requirement/${subtask.id}`)}
                        >
                          <td className="py-2.5 pr-2">
                            <span className="text-gray-900 dark:text-gray-100 font-medium truncate">{subtask.title}</span>
                          </td>
                          <td className="py-2.5">
                            <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">{subtask.story_type || '—'}</span>
                          </td>
                          <td className="py-2.5">
                            <span className={`inline-block px-2 py-0.5 text-xs rounded-full font-medium ${priorityColor[subtask.priority] || 'bg-gray-100 text-gray-600'}`}>
                              {subtask.priority?.charAt(0).toUpperCase() + subtask.priority?.slice(1)}
                            </span>
                          </td>
                          <td className="py-2.5 text-center">
                            <span className="text-gray-600 dark:text-gray-300">{subtask.story_points ?? '—'}</span>
                          </td>
                          <td className="py-2.5" onClick={e => e.stopPropagation()}>
                            {companyUsers.length > 0 && user?.role !== 'taskboard_user' && subtaskEditingField?.id === subtask.id && subtaskEditingField?.field === 'assigned_to' ? (
                              <select
                                autoFocus
                                value={subtask.assigned_to || ''}
                                onChange={e => handleSubtaskInlineEdit(subtask.id, 'assigned_to', e.target.value)}
                                onBlur={() => setSubtaskEditingField(null)}
                                className="w-full px-1.5 py-1 border border-indigo-300 dark:border-indigo-600 rounded text-xs dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                              >
                                <option value="">Unassigned</option>
                                {companyUsers.map(u => (
                                  <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>
                                ))}
                              </select>
                            ) : (
                              <button
                                onClick={() => companyUsers.length > 0 && user?.role !== 'taskboard_user' && setSubtaskEditingField({ id: subtask.id, field: 'assigned_to' })}
                                className={`text-xs text-gray-600 dark:text-gray-300 truncate max-w-full text-left ${companyUsers.length > 0 && user?.role !== 'taskboard_user' ? 'hover:text-indigo-600 dark:hover:text-indigo-400 cursor-pointer' : 'cursor-default'}`}
                              >
                                {subtask.assigned_to_name || 'Unassigned'}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-sm text-gray-400 italic text-center py-4">No subtasks yet</p>
                )}

                {/* Quick-add line */}
                <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                  {quickAddActive ? (
                    <div className="flex items-center gap-2">
                      <input
                        autoFocus
                        type="text"
                        value={quickAddTitle}
                        onChange={e => setQuickAddTitle(e.target.value)}
                        onKeyDown={handleQuickAddKeyDown}
                        onBlur={() => { if (!quickAddTitle.trim()) { setQuickAddActive(false); setQuickAddTitle(''); } }}
                        disabled={quickAddSubmitting}
                        className="flex-1 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                        placeholder="Subtask title... (Enter to add, Esc to cancel)"
                      />
                      <button
                        onClick={handleQuickAddSubtask}
                        disabled={quickAddSubmitting || !quickAddTitle.trim()}
                        className="px-3 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-md hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                      >
                        {quickAddSubmitting ? '...' : 'Add'}
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setQuickAddActive(true)}
                      className="flex items-center gap-1.5 text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-medium"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Add Subtask
                    </button>
                  )}
                </div>
                </>}
              </div>
            )}

            {/* Comments / Change Log Section */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center gap-2 cursor-pointer mb-4" onClick={() => toggleSection('comments')}>
                <svg className={`h-5 w-5 text-text-tertiary transition-transform duration-200 ${!collapsed.comments ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wider">Comments & Change Log</h2>
              </div>
              {!collapsed.comments && <>
              {/* Toggle buttons */}
              <div className="flex items-center gap-2 mb-4">
                <button
                  onClick={() => setActiveTab('comments')}
                  className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                    activeTab === 'comments'
                      ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  Comments
                  {commentCount > 0 && (
                    <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold rounded-full ${
                      activeTab === 'comments'
                        ? 'bg-white/20 text-white'
                        : 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400'
                    }`}>
                      {commentCount}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setActiveTab('changelog')}
                  className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                    activeTab === 'changelog'
                      ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  Change Log
                  {activities.length > 0 && (
                    <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold rounded-full ${
                      activeTab === 'changelog'
                        ? 'bg-white/20 text-white'
                        : 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400'
                    }`}>
                      {activities.length}
                    </span>
                  )}
                </button>
              </div>

              {/* Comments Tab */}
              {activeTab === 'comments' && (
                <div>
                  {/* New comment input */}
                  <div className="mb-4">
                    <textarea
                      value={newComment}
                      onChange={e => setNewComment(e.target.value)}
                      rows={3}
                      placeholder="Write a comment..."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:outline-none resize-y"
                    />
                    <div className="flex justify-end mt-2">
                      <button
                        onClick={handleSubmitComment}
                        disabled={submittingComment || !newComment.trim()}
                        className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                      >
                        {submittingComment ? 'Posting...' : 'Post Comment'}
                      </button>
                    </div>
                  </div>

                  {/* Comments list */}
                  {comments.length > 0 ? (
                    <div className="divide-y divide-gray-100 dark:divide-gray-700">
                      {comments.map(c => renderComment(c))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 italic text-center py-4">No comments yet. Be the first to comment!</p>
                  )}
                </div>
              )}

              {/* Change Log Tab */}
              {activeTab === 'changelog' && (
                <div>
                  {activities.length > 0 ? (
                    <div className="space-y-3">
                      {activities.map(activity => (
                        <div key={activity.id} className="flex items-start gap-3 py-2">
                          <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 flex items-center justify-center flex-shrink-0">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-900 dark:text-gray-100">
                              {activity.description}
                            </p>
                            <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
                              {activity.user_first_name && activity.user_last_name && (
                                <span>{activity.user_first_name} {activity.user_last_name}</span>
                              )}
                              <span>&middot;</span>
                              <span>{new Date(activity.created_at).toLocaleString()}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 italic text-center py-4">No activity recorded yet.</p>
                  )}
                </div>
              )}
              </>}
            </div>
          </div>

          {/* Sidebar (right 1/3) */}
          <div className="space-y-6">
            {/* Dates */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-5">
              <div className="flex items-center gap-2 cursor-pointer" onClick={() => toggleSection('dates')}>
                <svg className={`h-5 w-5 text-text-tertiary transition-transform duration-200 ${!collapsed.dates ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wider">Dates</h3>
              </div>
              {!collapsed.dates && <div className="space-y-3 mt-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Due Date</span>
                  <input
                    type="date"
                    value={editData.due_date}
                    onChange={e => setEditData({ ...editData, due_date: e.target.value })}
                    className={`px-2 py-1 border rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none ${
                      editData.due_date && new Date(editData.due_date) < new Date()
                        ? 'border-red-300 dark:border-red-600 text-red-600 dark:text-red-400 dark:bg-gray-700'
                        : 'border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100'
                    }`}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Created</span>
                  <span className="text-sm text-gray-900 dark:text-gray-100">
                    {requirement.created_at ? new Date(requirement.created_at).toLocaleDateString() : '—'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Updated</span>
                  <span className="text-sm text-gray-900 dark:text-gray-100">
                    {requirement.updated_at ? new Date(requirement.updated_at).toLocaleDateString() : '—'}
                  </span>
                </div>
              </div>}
            </div>

            {/* To Do Checklist */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-5">
              <div className="flex items-center gap-2 cursor-pointer" onClick={() => toggleSection('todo')}>
                <svg className={`h-5 w-5 text-text-tertiary transition-transform duration-200 ${!collapsed.todo ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wider">To Do</h3>
                <div className="flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-r from-indigo-100 to-blue-100 border border-indigo-200 dark:from-indigo-900/30 dark:to-blue-900/30 dark:border-indigo-800">
                  <span className="text-xs font-semibold text-indigo-700 dark:text-indigo-400">{checklistItems.length}</span>
                </div>
              </div>
              {!collapsed.todo && <div className="mt-3">
              {checklistItems.length > 0 ? (
                <div className="space-y-1.5 mb-3">
                  {checklistItems.map((item: any) => (
                    <div key={item.id} className="flex items-center gap-2 group">
                      <input
                        type="checkbox"
                        checked={item.is_completed}
                        onChange={() => handleToggleChecklistItem(item.id)}
                        className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                      />
                      <span className={`flex-1 text-sm ${
                        item.is_completed
                          ? 'line-through text-gray-400 dark:text-gray-500'
                          : 'text-gray-900 dark:text-gray-100'
                      }`}>
                        {item.title}
                      </span>
                      <button
                        onClick={() => handleDeleteChecklistItem(item.id)}
                        className="text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                        title="Remove"
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400 italic mb-3">No items yet</p>
              )}

              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newChecklistTitle}
                  onChange={e => setNewChecklistTitle(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleAddChecklistItem();
                    }
                  }}
                  disabled={checklistSubmitting}
                  className="flex-1 px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  placeholder="Add item..."
                />
                <button
                  onClick={handleAddChecklistItem}
                  disabled={checklistSubmitting || !newChecklistTitle.trim()}
                  className="px-3 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-md hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                >
                  {checklistSubmitting ? '...' : 'Add'}
                </button>
              </div>
              </div>}
            </div>

            {/* Sprint History */}
            {sprintHistory.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-5">
                <div className="flex items-center gap-2 cursor-pointer" onClick={() => toggleSection('sprintHistory')}>
                  <svg className={`h-5 w-5 text-text-tertiary transition-transform duration-200 ${!collapsed.sprintHistory ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wider">Sprint History</h3>
                  <div className="flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-r from-indigo-100 to-blue-100 border border-indigo-200 dark:from-indigo-900/30 dark:to-blue-900/30 dark:border-indigo-800">
                    <span className="text-xs font-semibold text-indigo-700 dark:text-indigo-400">{sprintHistory.length}</span>
                  </div>
                </div>
                {!collapsed.sprintHistory && (
                  <div className="mt-3 space-y-2">
                    {sprintHistory.map((entry: any) => (
                      <div key={entry.id} className="flex items-start gap-2 py-2 border-b border-gray-100 dark:border-gray-700/50 last:border-0">
                        <div className="flex-shrink-0 mt-0.5">
                          {entry.outcome === 'completed' && (
                            <div className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                              <svg className="w-3 h-3 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                            </div>
                          )}
                          {entry.outcome === 'carried_over' && (
                            <div className="w-5 h-5 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                              <svg className="w-3 h-3 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
                            </div>
                          )}
                          {entry.outcome === 'moved_to_backlog' && (
                            <div className="w-5 h-5 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                              <svg className="w-3 h-3 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 12H5" /></svg>
                            </div>
                          )}
                          {!entry.outcome && (
                            <div className="w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                              <div className="w-2 h-2 rounded-full bg-blue-500 dark:bg-blue-400"></div>
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{entry.sprint_name}</p>
                          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            {entry.outcome ? (
                              <span className={`inline-block px-1.5 py-0.5 text-xs rounded font-medium ${
                                entry.outcome === 'completed'
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                  : entry.outcome === 'carried_over'
                                  ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                                  : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                              }`}>
                                {entry.outcome === 'completed' ? 'Completed' : entry.outcome === 'carried_over' ? 'Carried Over' : 'Backlog'}
                              </span>
                            ) : (
                              <span className="inline-block px-1.5 py-0.5 text-xs rounded font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                                Active
                              </span>
                            )}
                            {entry.story_points_snapshot != null && (
                              <span className="text-xs text-gray-400">{entry.story_points_snapshot} pts</span>
                            )}
                            {entry.target_sprint_name && (
                              <span className="text-xs text-gray-400">
                                &rarr; {entry.target_sprint_name}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-400 mt-0.5">
                            {new Date(entry.added_at).toLocaleDateString()}
                            {entry.removed_at && ` — ${new Date(entry.removed_at).toLocaleDateString()}`}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* People */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-5">
              <div className="flex items-center gap-2 cursor-pointer" onClick={() => toggleSection('team')}>
                <svg className={`h-5 w-5 text-text-tertiary transition-transform duration-200 ${!collapsed.team ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wider">Team</h3>
              </div>
              {!collapsed.team && <div className="space-y-3 mt-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Created By</span>
                  <span className="text-sm text-gray-900 dark:text-gray-100">{requirement.created_by_name || '—'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Assignee</span>
                  {companyUsers.length > 0 && user?.role !== 'taskboard_user' ? (
                    <select
                      value={editData.assigned_to}
                      onChange={e => setEditData({ ...editData, assigned_to: e.target.value })}
                      className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                    >
                      <option value="">Unassigned</option>
                      {companyUsers.map(u => (
                        <option key={u.id} value={u.id}>
                          {u.first_name} {u.last_name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="text-sm text-gray-900 dark:text-gray-100">
                      {requirement.assigned_to_name || 'Unassigned'}
                    </span>
                  )}
                </div>
              </div>}
            </div>

            {/* Time Tracking */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-5">
              <div className="flex items-center gap-2 cursor-pointer" onClick={() => toggleSection('time')}>
                <svg className={`h-5 w-5 text-text-tertiary transition-transform duration-200 ${!collapsed.time ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wider">Time Tracking</h3>
              </div>
              {!collapsed.time && <div className="mt-3">
              <div className="flex gap-2 mb-3">
                <input
                  type="number"
                  min="0.25"
                  step="0.25"
                  value={newLogHours}
                  onChange={e => setNewLogHours(e.target.value)}
                  className="w-16 px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  placeholder="Hrs"
                />
                <input
                  type="text"
                  value={newLogDescription}
                  onChange={e => setNewLogDescription(e.target.value)}
                  className="flex-1 px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  placeholder="Note"
                />
                <button
                  onClick={handleLogTime}
                  disabled={loggingTime || !newLogHours}
                  className="px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors"
                >
                  {loggingTime ? '...' : 'Log'}
                </button>
              </div>

              <div className="flex items-center justify-between text-sm py-2 border-b border-gray-100 dark:border-gray-700 mb-3">
                <span className="text-gray-500 dark:text-gray-400">Spent / Remaining</span>
                <span className="text-gray-900 dark:text-gray-100 font-medium">
                  {requirement.time_spent_hours || 0}h / {requirement.time_remaining_hours ?? '—'}h
                </span>
              </div>

              {timeLogs.length > 0 ? (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {timeLogs.map((log: any) => (
                    <div key={log.id} className="flex items-center justify-between text-xs bg-gray-50 dark:bg-gray-700/50 rounded p-2">
                      <div className="min-w-0">
                        <span className="font-medium text-gray-900 dark:text-gray-100">{log.hours}h</span>
                        <span className="text-gray-400 ml-1">{log.user_name?.split(' ')[0]}</span>
                        {log.description && (
                          <p className="text-gray-400 truncate mt-0.5">{log.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                        <span className="text-gray-400">{new Date(log.logged_date).toLocaleDateString()}</span>
                        <button
                          onClick={() => handleDeleteLog(log.id)}
                          className="text-red-400 hover:text-red-600 ml-1"
                          title="Remove"
                        >
                          &times;
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400 italic">No time logged.</p>
              )}
              </div>}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Attachment Confirmation Modal */}
      {attachmentToDelete && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75" onClick={() => setAttachmentToDelete(null)} />
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">Delete Attachment</h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Are you sure you want to delete <span className="font-semibold text-gray-900 dark:text-gray-100">"{attachmentToDelete.original_name}"</span>? This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700/50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={confirmDeleteAttachment}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Delete
                </button>
                <button
                  onClick={() => setAttachmentToDelete(null)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default TaskBoardRequirementDetail;
